import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 5 — Incentive & Deduction templates. Admin-curated catalog that
// SalaryPage reads via the "Quick Apply Template" button.

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const EMPTY = { name: '', template_type: 'incentive', default_amount: 0, description: '' };

export default function IncentivesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const refresh = () => api.get('/incentive-templates').then(r => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { refresh(); }, []);

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center"><p className="text-sm text-[#EF4444]">Admin access required</p></div>;
  }

  const openNew = () => { setEditing({}); setForm(EMPTY); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t, default_amount: t.default_amount || 0 }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      if (editing?.id) {
        await api.put(`/incentive-templates/${editing.id}`, { ...form, default_amount: parseFloat(form.default_amount) || 0 });
        toast.success('Template updated');
      } else {
        await api.post('/incentive-templates', { ...form, default_amount: parseFloat(form.default_amount) || 0 });
        toast.success('Template added');
      }
      setEditing(null);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/incentive-templates/${t.id}`);
      toast.success('Deleted');
      refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="incentives-page">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#52525B]">Pre-define common incentive and deduction lines so admin can apply them in one click during salary generation.</p>
        <Button onClick={openNew} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="new-template-btn">
          <Plus size={16} className="mr-1.5" /> New Template
        </Button>
      </div>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="text-sm text-[#52525B] text-center py-8">No templates yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Type</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Default Amount</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Notes</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(t => (
                  <TableRow key={t.id} className="border-[#E4E4E7]" data-testid={`tpl-row-${t.id}`}>
                    <TableCell className="text-sm font-medium">{t.name}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-md text-[10px] ${t.template_type === 'incentive' ? 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' : 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]'}`} variant="outline">
                        {t.template_type === 'incentive' ? 'Incentive' : 'Deduction'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmt(t.default_amount)}</TableCell>
                    <TableCell className="text-xs text-[#52525B] max-w-[300px] truncate" title={t.description}>{t.description || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openEdit(t)} data-testid={`edit-tpl-${t.id}`}><PencilSimple size={14} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#EF4444]" onClick={() => remove(t)} data-testid={`delete-tpl-${t.id}`}><Trash size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={v => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogDescription>One-click incentive or deduction line for salary slips.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" data-testid="tpl-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Type</Label>
                <Select value={form.template_type} onValueChange={v => setForm({ ...form, template_type: v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="tpl-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incentive">Incentive</SelectItem>
                    <SelectItem value="deduction">Deduction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Default Amount</Label>
                <Input type="number" min={0} value={form.default_amount} onChange={e => setForm({ ...form, default_amount: e.target.value })} className="rounded-lg mt-1" data-testid="tpl-amount" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Notes</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-lg mt-1" data-testid="tpl-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-tpl-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
